/* eslint-disable react/no-array-index-key */
import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { trans, useSoyuzClientConfig } from '@spotahome/soyuz/client';
import { Button, useModalHandle } from '@spotahome/ui-library';
import Metrics from '@spotahome/soyuz-metrics';

import { addAlert, editAlert } from '@components/AlertModal/mutations';
import AlertModal from '@components/AlertModal';
import { getCityList } from '@utils/cities';
import { sanitizeUrl } from '@utils/sanitizeUrl';
import AlertShape from '@alerts/shapes/alert';

import { removeAlert as requestRemoveAlert } from '@gqlClient/mutations/alerts';
import getGqlClient from '@gqlClient/client';

import { DELETE_ALERT, EDIT_ALERT } from '@alerts/constants';

import SideMenu, {
  sideMenuSections
} from '../../../../common/components/SideMenu';

import AlertsNotLogged from '../AlertsNotLogged';
import DeleteAlertModal from '../DeleteAlertModal';
import AlertWrapper from '../AlertWrapper';
import Alert from '../Alert';

import './styles.scss';

async function removeAlert(id) {
  try {
    await requestRemoveAlert(getGqlClient())(id);
    return id;
  } catch (err) {
    console.error(err);
    return null;
  }
}

const isIdInAlerts = (id, alerts) => alerts.find(alert => alert.id === id);

const handleHomeButton = () => {
  Metrics.ga.sendEvent('My alerts', 'go to home page button', 'click button');
};

const Alerts = memo(
  ({
    isLogged,
    initialQuery: { id, action },
    userInfo,
    cities,
    alerts,
    onAdd,
    onEdit,
    onRemove
  }) => {
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();
    const citiesList = getCityList(cities);
    const {
      featureToggle: { enableProfileMenu }
    } = useSoyuzClientConfig();

    const openModal = ({ selectedId: selectIdToModify } = {}) => {
      Metrics.ga.sendEvent(
        'My alerts page',
        'create new alert',
        'click button'
      );

      setIsDeleteModalOpen(false);
      if (selectIdToModify) {
        setSelectedId(selectIdToModify);
      }
      handleModalOpen();
    };

    const resetInitialState = () => {
      handleModalClose();
      setSelectedId(null);
      setIsDeleteModalOpen(false);
      setIsLoading(false);
    };

    const handleClose = () => {
      resetInitialState();
    };

    const handleConfirm = async alertData => {
      Metrics.ga.sendEvent(
        'My alerts Modal',
        'confirm create alert button',
        'click button'
      );

      const idToEdit = selectedId;
      if (idToEdit) {
        const newAlert = await editAlert(idToEdit, alertData);
        resetInitialState();
        onEdit(idToEdit, newAlert);
      } else {
        const newAlert = await addAlert(alertData);
        resetInitialState();
        onAdd(newAlert);
      }
    };

    const handleDeleteModal = selectedIdToDelete => {
      setSelectedId(selectedIdToDelete);
      setIsDeleteModalOpen(true);
    };

    const handleEditModal = selectIdToEdit => {
      openModal({ selectedId: selectIdToEdit });
    };

    const handleCreateModal = () => {
      openModal();
    };

    const queryActions = {
      [DELETE_ALERT]: (...args) => handleDeleteModal(...args),
      [EDIT_ALERT]: (...args) => handleEditModal(...args),
      noop: () => {}
    };

    useEffect(() => {
      if (action && isIdInAlerts(id, alerts)) {
        const queryAction = queryActions[action] || queryActions.noop;
        sanitizeUrl();
        queryAction(id);
      }
    }, [action, alerts, id, queryActions]);

    const confirmDelete = async () => {
      setIsLoading(true);
      const idToDelete = selectedId;
      try {
        const removedId = await removeAlert(idToDelete);
        resetInitialState();
        onRemove(removedId);
      } catch (error) {
        setIsLoading(false);
      }
    };

    const renderNotLogged = () => (
      <AlertsNotLogged
        handleModalOpen={openModal}
        handleModalClose={handleClose}
        isModalOpen={isModalOpen}
      />
    );

    const renderContent = () => {
      const alertSelected = alerts.find(
        ({ id: alertId }) => alertId === selectedId
      );

      const { email } = userInfo;

      return (
        <>
          <div className="alerts-list__wrapper">
            <p className="alerts-list__disclaimer">
              {trans('alerts.body.sending_alerts', { email })}
            </p>
            {alerts.map((alert, key) => (
              <Alert
                {...alert}
                key={`${key}-${alert.id}`}
                onEdit={handleEditModal}
                onRemove={handleDeleteModal}
                cityInfo={citiesList[alert.cityId]}
              />
            ))}
            <AlertWrapper
              city={trans('alerts.new_alert.title')}
              content={trans('alerts.new_alert.content')}
              renderButtons={() => (
                <Button
                  className="alert__create-new"
                  data-test="alert-create-new"
                  size="big"
                  onClick={handleCreateModal}
                >
                  {trans('alerts.new_alert.create')}
                </Button>
              )}
            />
            <p className="alerts-list__disclaimer">
              {trans('alerts.body.reminder')}
            </p>
            <div className="alerts-list__back-wrapper">
              <Button
                alt
                size="big"
                href="/"
                className="alerts-list__back"
                onClick={handleHomeButton}
              >
                {trans('alerts.body.continue_searching')}
              </Button>
            </div>
          </div>
          <DeleteAlertModal
            isLoading={isLoading}
            isOpen={isDeleteModalOpen}
            cancelDelete={resetInitialState}
            confirmDelete={confirmDelete}
          />
          <AlertModal
            isModalOpen={isModalOpen}
            onConfirm={handleConfirm}
            onClose={handleClose}
            alert={alertSelected}
            cities={cities}
          />
        </>
      );
    };

    return (
      <div className="alerts-list">
        <h1 className="my-alerts__title">{trans('alerts.body.title')}</h1>

        <div className="my-alerts__results">
          <div className="my-alerts__main">
            {enableProfileMenu && isLogged && (
              <SideMenu
                user={userInfo}
                selectedOption={sideMenuSections.MY_ALERTS}
              />
            )}
            {isLogged && alerts ? renderContent() : renderNotLogged()}
          </div>
        </div>
      </div>
    );
  }
);

Alerts.propTypes = {
  isLogged: PropTypes.bool,
  initialQuery: PropTypes.shape({
    id: PropTypes.string,
    action: PropTypes.string
  }),
  userInfo: PropTypes.shape({
    email: PropTypes.string
  }).isRequired,
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      currencyIsoCode: PropTypes.string
    })
  ).isRequired,
  alerts: PropTypes.arrayOf(PropTypes.shape(AlertShape)).isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  clientConfig: PropTypes.shape({
    featureToggle: PropTypes.shape({
      enableProfileMenu: PropTypes.bool.isRequired
    })
  }).isRequired
};

Alerts.defaultProps = {
  isLogged: null,
  initialQuery: {}
};

export default Alerts;
